import React, { useEffect, useRef, useState } from 'react';
import { UrlsByBandType, UrlsByTimeType, getGeeUrl } from '../api/getMapUrl';
import { createTimeString, convertAssetIDToLocalTime } from '../../../utils/timeRelatedFunctions';

type urlsType = {
  imageId: string;
  urlList: UrlsByBandType | null
}

const useGeeUrl = ({imageIdList, increment, delta}:{imageIdList:string[], increment:number, delta:number}) => {
  const [isTileURLLoading, setIsTileURLLoading] = useState<boolean>(false);
  // useEffectが２回呼ばれるのでその対策
  const isTileURLLoadingRef = useRef<boolean>(false);
  const [geeUrl, setGeeUrl] = useState<UrlsByTimeType | null>(null);
  
  useEffect(() => {
    if (isTileURLLoadingRef.current) return;
    // タイル画像が保存されていない場合.
    if (imageIdList.length < 1) return;
    
    isTileURLLoadingRef.current = true;
    setIsTileURLLoading(true);
    const generatedTimeList = generateTimeList(increment, delta);
    
    // imageIdList全て取る必要はなく, 最初と最後の差が33h以内ならその2つだけ取れば辞書は作れる.
    // なので, imageIdListの一番新しいものとそこから33h以内の一番古いだけをfetchして繋げる. 
    const idListToFetch = filterLatestAndOldestWithin33Hours({imageIdList, increment})

    Promise.all(idListToFetch.map(imageId => 
      getGeeUrl({ imageId, timeList:generatedTimeList }).then((res) => {
        return { urlList: res ? res.map_url : [], imageId }; // ここでは各URLを返すだけで、状態の更新はしない
      })
    )).then(urls => {
      const geeURLsFor36h = bundleURLs(urls)
      if (!geeURLsFor36h) {
        return
      }
      setGeeUrl(prev => ({...prev, ...geeURLsFor36h}));
    }).finally(() => {
      isTileURLLoadingRef.current = false;
      setIsTileURLLoading(false);
    });
}, [imageIdList, increment, delta]);

  return { isTileURLLoading, geeUrlDict:geeUrl };
};

export default useGeeUrl;

const bundleURLs = (urls:urlsType[]) => {
  if (!urls){
    return 
  }
  let sortedURLs = JSON.parse(JSON.stringify(urls)) as urlsType[]
  let urlsWithTimeKeys = {} as UrlsByTimeType
  // sortedURLsは起算時刻が新しい順に出てくる. 
  for (const item of sortedURLs){
    const calculatedTime = convertAssetIDToLocalTime(item.imageId)
    if (!item.urlList || !calculatedTime){
      return 
    }
    for (const [urlObjKey, value] of Object.entries(item.urlList)){
      if (urlObjKey === 'asset'){
        continue
      }
      // urlObjKeyは起算時刻からの差分（時間）を保存しているのでそれをもとに時刻をキーにする. 
      const deltaHours = Number(urlObjKey)
      const copiedTime = new Date(calculatedTime)
      copiedTime.setTime(copiedTime.getTime() + deltaHours * 60 * 60 * 1000)
      const timeString = createTimeString(copiedTime)
      // すでに値がある場合(より新しいデータが入っている場合)はパス
      if (!!urlsWithTimeKeys[timeString]){
        continue
      }
      urlsWithTimeKeys[timeString] = value
    }
  }
  return urlsWithTimeKeys
}

const generateTimeList = (increment:number, delta:number) => 
  Array.from({ length: 40 }, (_, i) => i).filter(n => n % increment === delta).map(n => n)

function filterLatestAndOldestWithin33Hours({imageIdList, increment}:{imageIdList: string[], increment: number}): string[] {
  // 日時部分を取得して変換する関数
  function extractDate(str: string): Date {
      const dateString = str.slice(-10); // "yyyymmddhh" の部分を抽出
      const year = parseInt(dateString.slice(0, 4), 10);
      const month = parseInt(dateString.slice(4, 6), 10) - 1; // JavaScriptの月は0-11
      const day = parseInt(dateString.slice(6, 8), 10);
      const hour = parseInt(dateString.slice(8, 10), 10);
      return new Date(year, month, day, hour);
  }

  // 日時でソートする
  const sortedImageIdList = [...imageIdList].sort((a, b) => extractDate(b).getTime() - extractDate(a).getTime());

  const latestDate: Date = extractDate(sortedImageIdList[0]);
  const thresholdDate: Date = new Date(latestDate.getTime() - 27 * 60 * 60 * 1000); // 33時間前を計算
  // 最も新しいものと、27h以内で最も古いものを見つける
  let oldestWithin27Hours: string = sortedImageIdList[0];
  for (let i=0; i < sortedImageIdList.length; i++) {

    const olderDate: Date = extractDate(sortedImageIdList[i]);
    const timeDifference = Math.abs(latestDate.getTime() - olderDate.getTime());
    if (olderDate >= thresholdDate && timeDifference % (increment * 60 * 60 * 1000) === 0) {
        oldestWithin27Hours = sortedImageIdList[i];
    } else {
        continue
    }
  }

  // 長さが2未満の場合は最新のデータだけ返す
  if (oldestWithin27Hours === sortedImageIdList[0]) {
    return [sortedImageIdList[0]];
  } else {
    return [sortedImageIdList[0], oldestWithin27Hours];
  }
}
